/**
 * SmsSendModal — « Envoyer par texto » d'un document existant (vente du cabinet · document à signer) : charge le texte
 * proposé (POST /admin/sms-outreach/preview), laisse l'admin valider / modifier le numéro et le texte, puis envoie
 * (POST /admin/sms-outreach/send → Twilio, journal « Suivi comms »).
 * kind : sale | fiscal | tax_report | contract | questionnaire | document — id : identifiant du document.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, Send, Smartphone, X } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import SmsComposer from "./SmsComposer";

const errMsg = (e, fb = "Erreur") => e?.response?.data?.detail || e?.message || fb;

export default function SmsSendModal({ kind, id, title, onClose, onSent }) {
  const [p, setP] = useState(null);
  const [to, setTo] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    api.post("/admin/sms-outreach/preview", { kind, id, origin_url: window.location.origin })
      .then((r) => { if (!alive) return; setP(r.data); setTo(r.data.to || ""); setText(r.data.text || ""); })
      .catch((e) => alive && setErr(errMsg(e, "Aperçu indisponible")));
    return () => { alive = false; };
  }, [kind, id]);

  const send = async () => {
    if (!to.trim()) { toast.error("Entrez le numéro de cellulaire"); return; }
    if (!text.trim()) { toast.error("Le texte du texto est vide"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/sms-outreach/send", { kind, id, to: to.trim(), text, origin_url: window.location.origin });
      toast.success(`Texto envoyé à ${r.data.to}`);
      onSent?.(r.data);
      onClose();
    } catch (e) { toast.error(errMsg(e, "Texto non envoyé")); }
    finally { setBusy(false); }
  };

  // Portail vers <body> : échappe aux styles hérités (cellule d'actions alignée à droite, overflow des tableaux)
  return createPortal(
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4 text-left" onClick={onClose} data-testid="sms-send-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-orange-50 to-amber-50">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2"><Smartphone className="w-5 h-5 text-orange-600" /> Envoyer par texto{title ? ` — ${title}` : ""}</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-700" data-testid="sms-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="sms-modal-error">{err}</div>}
          {!p && !err && <div className="py-6 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
          {p && (
            <>
              {p.last_sms_at && <div className="text-[11px] text-slate-500" data-testid="sms-modal-last">Déjà envoyé par texto le {String(p.last_sms_at).slice(0, 16).replace("T", " ")}</div>}
              <SmsComposer to={to} onTo={setTo} text={text} onText={setText} links={p.links} phoneMissing={p.phone_missing} clientName={p.client_name} idPrefix="sms-modal" />
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm hover:bg-slate-50" data-testid="sms-modal-cancel">Annuler</button>
          <button onClick={send} disabled={busy || !p || !to.trim() || !text.trim()}
            className="px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-semibold hover:bg-orange-700 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="sms-modal-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer le texto
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
